import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { api, formatINR } from "@/lib/api";
import { toast } from "sonner";
import { LogOut, Package, UserRound } from "lucide-react";

const ORDER_STATUSES = ["placed", "processing", "shipped", "delivered"];

function OrderStatus({ status }) {
  const current = ORDER_STATUSES.indexOf(status);
  if (status === "cancelled") {
    return <div className="text-sm text-[#E63946]">Order cancelled</div>;
  }
  return (
    <ol className="grid grid-cols-4 gap-2" aria-label={`Order status: ${status}`}>
      {ORDER_STATUSES.map((step, index) => (
        <li key={step} className="min-w-0">
          <div className={`h-1 mb-2 ${index <= current ? "bg-[#E63946]" : "bg-white/15"}`} />
          <span className={`text-[10px] uppercase ${index <= current ? "text-white" : "text-white/40"}`}>{step}</span>
        </li>
      ))}
    </ol>
  );
}

function OrderCard({ order }) {
  const address = order.shipping || {};
  return (
    <article className="border border-white/10 bg-[#111]">
      <header className="grid sm:grid-cols-[1fr_auto] gap-4 p-5 border-b border-white/10">
        <div>
          <div className="overline text-white/50">Order number</div>
          <h2 className="font-display text-xl">{order.order_number}</h2>
          <div className="text-xs text-white/50 mt-1">{new Date(order.created_at).toLocaleString()}</div>
        </div>
        <div className="sm:w-56">
          <OrderStatus status={order.order_status} />
        </div>
      </header>

      <div className="p-5 grid lg:grid-cols-[1fr_280px] gap-6">
        <div className="min-w-0">
          <h3 className="overline text-white/50 mb-3">Items</h3>
          <ul className="divide-y divide-white/10">
            {(order.items || []).map((item, index) => {
              const images = item.images?.length ? item.images : item.image ? [item.image] : [];
              return (
                <li key={`${item.product_id}-${index}`} className="py-4 first:pt-0 last:pb-0">
                  <div className="flex gap-3">
                    {images.length > 0 && (
                      <div className="flex flex-wrap content-start gap-1 w-[68px] shrink-0">
                        {images.map((image, imageIndex) => (
                          <img key={`${image}-${imageIndex}`} src={image} alt={`${item.name} image ${imageIndex + 1}`} loading="lazy" className="w-8 h-8 object-cover bg-black border border-white/10" />
                        ))}
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap justify-between gap-x-4 gap-y-1">
                        <div className="font-medium">{item.name}</div>
                        <div className="text-sm">{formatINR(item.price * item.quantity)}</div>
                      </div>
                      <div className="text-xs text-white/50 mt-1">
                        Qty {item.quantity} · {formatINR(item.price)} each
                        {item.brand ? ` · ${item.brand}` : ""}
                        {item.category ? ` · ${item.category}` : ""}
                      </div>
                      {(item.description || item.features?.length || Object.keys(item.specs || {}).length > 0) && (
                        <details className="mt-2 text-xs text-white/60">
                          <summary className="cursor-pointer hover:text-white">Product details</summary>
                          {item.description && <p className="mt-2 whitespace-pre-line">{item.description}</p>}
                          {item.features?.length > 0 && <ul className="list-disc pl-4 mt-2">{item.features.map((feature) => <li key={feature}>{feature}</li>)}</ul>}
                          {Object.entries(item.specs || {}).map(([key, value]) => <div key={key} className="mt-1">{key}: {value}</div>)}
                        </details>
                      )}
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>

        <aside className="border-t lg:border-t-0 lg:border-l border-white/10 pt-4 lg:pt-0 lg:pl-5">
          <h3 className="overline text-white/50 mb-3">Delivery details</h3>
          <div className="text-sm">{address.full_name}</div>
          <div className="text-sm text-white/70">{address.phone}</div>
          <div className="text-sm text-white/70 break-all">{address.email}</div>
          <address className="text-sm text-white/60 not-italic mt-2">
            {address.address_line1}{address.address_line2 ? `, ${address.address_line2}` : ""}<br />
            {address.city}, {address.state} {address.pincode}<br />
            {address.country}
          </address>
          <div className="border-t border-white/10 mt-4 pt-3 space-y-2 text-sm">
            <div className="flex justify-between text-white/60"><span>Payment</span><span className="text-white">{order.payment_status}</span></div>
            <div className="flex justify-between text-white/60"><span>Subtotal</span><span>{formatINR(order.subtotal)}</span></div>
            <div className="flex justify-between text-white/60"><span>Shipping</span><span>{formatINR(order.shipping_cost)}</span></div>
            <div className="flex justify-between font-display text-lg"><span>Total</span><span>{formatINR(order.total)}</span></div>
          </div>
        </aside>
      </div>
    </article>
  );
}

export default function Account() {
  const [customer, setCustomer] = useState(null);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [registering, setRegistering] = useState(false);
  const [error, setError] = useState("");
  const [form, setForm] = useState({ full_name: "", phone: "", email: "", password: "" });
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/customer/me")
      .then((response) => {
        setCustomer(response.data.customer);
        return api.get("/customer/orders");
      })
      .then((response) => setOrders(response.data.items || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const submitAuth = async (event) => {
    event.preventDefault();
    setSubmitting(true);
    setError("");
    try {
      const endpoint = registering ? "/customer/register" : "/customer/login";
      const payload = registering
        ? form
        : { email: form.email, password: form.password };
      const response = await api.post(endpoint, payload);
      setCustomer(response.data.customer);
      toast.success(registering ? "Account created" : "Signed in");
      if (location.state?.returnTo) navigate(location.state.returnTo, { replace: true });
      else api.get("/customer/orders").then((result) => setOrders(result.data.items || []));
    } catch (requestError) {
      const detail = requestError.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Could not sign in. Check your details and try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const signOut = async () => {
    await api.post("/customer/logout").catch(() => {});
    setCustomer(null);
    setOrders([]);
    toast.success("Signed out");
  };

  if (loading) {
    return <main className="max-w-5xl mx-auto px-4 py-16 text-white/60">Loading account...</main>;
  }

  if (!customer) {
    return (
      <main className="max-w-lg mx-auto px-4 py-12 md:py-20" data-testid="account-page">
        <div className="overline text-[#E63946] mb-3">Customer account</div>
        <h1 className="font-display text-3xl md:text-4xl mb-7">{registering ? "CREATE ACCOUNT" : "SIGN IN"}</h1>
        <form onSubmit={submitAuth} className="border border-white/10 bg-[#111] p-5 md:p-7 space-y-4">
          {registering && <>
            <label className="block text-xs text-white/60">Full name<input required autoComplete="name" className="sa-input mt-1" value={form.full_name} onChange={(event) => setForm({ ...form, full_name: event.target.value })} /></label>
            <label className="block text-xs text-white/60">Phone<input autoComplete="tel" className="sa-input mt-1" value={form.phone} onChange={(event) => setForm({ ...form, phone: event.target.value })} /></label>
          </>}
          <label className="block text-xs text-white/60">Email<input required type="email" autoComplete="email" className="sa-input mt-1" value={form.email} onChange={(event) => setForm({ ...form, email: event.target.value })} /></label>
          <label className="block text-xs text-white/60">Password<input required minLength={registering ? 8 : undefined} type="password" autoComplete={registering ? "new-password" : "current-password"} className="sa-input mt-1" value={form.password} onChange={(event) => setForm({ ...form, password: event.target.value })} /></label>
          {error && <p role="alert" className="text-sm text-[#E63946]">{error}</p>}
          <button disabled={submitting} className="sa-btn sa-btn-primary w-full py-3" data-testid="customer-auth-submit">
            {submitting ? "PLEASE WAIT..." : registering ? "CREATE ACCOUNT" : "SIGN IN"}
          </button>
          <button type="button" onClick={() => { setRegistering(!registering); setError(""); }} className="text-sm text-white/60 hover:text-white w-full py-2">
            {registering ? "Already have an account? Sign in" : "New here? Create an account"}
          </button>
        </form>
        <Link to="/shop" className="inline-block mt-5 text-sm text-white/50 hover:text-white">Continue shopping</Link>
      </main>
    );
  }

  return (
    <main className="max-w-[1200px] mx-auto px-4 md:px-8 py-10 md:py-14" data-testid="account-page">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div>
          <div className="overline text-[#E63946] mb-2">Customer account</div>
          <h1 className="font-display text-3xl md:text-4xl">YOUR ORDERS</h1>
          <p className="text-sm text-white/55 mt-2"><UserRound size={15} className="inline mr-1" />{customer.full_name} · {customer.email}</p>
        </div>
        <button onClick={signOut} className="sa-btn sa-btn-outline"><LogOut size={15} /> SIGN OUT</button>
      </div>
      {orders.length === 0 ? (
        <div className="border border-dashed border-white/15 py-16 px-6 text-center">
          <Package size={28} className="mx-auto text-white/40 mb-3" />
          <p className="text-white/60 mb-5">No orders are linked to this account yet.</p>
          <Link to="/shop" className="sa-btn sa-btn-primary">SHOP PRODUCTS</Link>
        </div>
      ) : (
        <div className="space-y-5">{orders.map((order) => <OrderCard key={order.id} order={order} />)}</div>
      )}
    </main>
  );
}